import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, Users, Wallet, TrendingUp, Settings, LogOut,
  Search, Filter, Edit, Trash2, Eye, EyeOff,
  Plus, Minus, DollarSign, AlertTriangle, Check, X,
  Copy, Download,
  Bell, Send, Activity,
  ArrowUpRight, ArrowDownRight, AlertCircle, CheckCircle, Key,
  Monitor, Smartphone, Tablet, Globe, RefreshCw,
  KeyRound, Lock, Mail
} from 'lucide-react';
import { useAuth } from '../App';
import * as api from '../services/api';
import toast from 'react-hot-toast';
import './AdminPanel.css';

// Available crypto assets with their details
const availableCryptoAssets = [
  { symbol: 'BTC', name: 'Bitcoin', icon: '/Crypto-logos/Bitcoin-Logo.webp', color: '#F7931A' },
  { symbol: 'ETH', name: 'Ethereum', icon: '/Crypto-logos/Ethereum-logo.webp', color: '#627EEA' },
  { symbol: 'SOL', name: 'Solana', icon: '/Crypto-logos/Solana-logo.webp', color: '#00FFA3' },
  { symbol: 'BNB', name: 'BNB', icon: '/Crypto-logos/bnb-logo.webp', color: '#F3BA2F' },
  { symbol: 'MATIC', name: 'Polygon', icon: '/Crypto-logos/matic-token-icon.webp', color: '#8247E5' },
  { symbol: 'XRP', name: 'Ripple', icon: '/Crypto-logos/XRP-logo.webp', color: '#23292F' },
  { symbol: 'ADA', name: 'Cardano', icon: '/Crypto-logos/Cardano-logo.webp', color: '#0033AD' },
  { symbol: 'DOGE', name: 'Dogecoin', icon: '/Crypto-logos/Doge-Logo.webp', color: '#C3A634' },
  { symbol: 'DOT', name: 'Polkadot', icon: '/Crypto-logos/Polkadot-logo.webp', color: '#E6007A' },
  { symbol: 'LINK', name: 'Chainlink', icon: '/Crypto-logos/Chainlink-logo.webp', color: '#2A5ADA' },
  { symbol: 'LTC', name: 'Litecoin', icon: '/Crypto-logos/litecoin.webp', color: '#BFBBBB' },
  { symbol: 'USDT', name: 'Tether', icon: '/Crypto-logos/SizeXL-TypeCoin-AssetUSDT.webp', color: '#26A17B' },
  { symbol: 'USDC', name: 'USD Coin', icon: '/Crypto-logos/USD_Coin_icon.webp', color: '#2775CA' },
  { symbol: 'UNI', name: 'Uniswap', icon: '/Crypto-logos/Uniswap-logo.webp', color: '#FF007A' },
  { symbol: 'SHIB', name: 'Shiba Inu', icon: '/Crypto-logos/shiba.webp', color: '#FFA409' },
  { symbol: 'TRX', name: 'Tron', icon: '/Crypto-logos/Tron-Logo.webp', color: '#FF0013' },
  { symbol: 'DAI', name: 'Dai', icon: '/Crypto-logos/Dai-logo.webp', color: '#F5AC37' },
  { symbol: 'WBTC', name: 'Wrapped BTC', icon: '/Crypto-logos/Wrapped-Bitcoin-logo-1.webp', color: '#F09242' },
  { symbol: 'ARB', name: 'Arbitrum', icon: '/Crypto-logos/Arbitrum-logo.webp', color: '#28A0F0' },
  { symbol: 'OP', name: 'Optimism', icon: '/Crypto-logos/Optimism.webp', color: '#FF0420' },
];

const coingeckoIds = {
  BTC: 'bitcoin', ETH: 'ethereum', SOL: 'solana', BNB: 'binancecoin', MATIC: 'matic-network',
  XRP: 'ripple', ADA: 'cardano', DOGE: 'dogecoin', DOT: 'polkadot', LINK: 'chainlink',
  LTC: 'litecoin', USDT: 'tether', USDC: 'usd-coin', UNI: 'uniswap', SHIB: 'shiba-inu',
  TRX: 'tron', DAI: 'dai', WBTC: 'wrapped-bitcoin', ARB: 'arbitrum', OP: 'optimism'
};

const AdminPanel = () => {
  const { 
    admin, users, transactions, notifications, activityLogs,
    logoutAdmin, updateUserBalance, updateUserAssets,
    updateUserStatus, deleteUser,
    sendNotification, sendBulkNotification, deleteNotification,
    exportUsersToCSV, exportTransactionsToCSV,
    dataLoaded
  } = useAuth();
  
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showBulkNotificationModal, setShowBulkNotificationModal] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [showSensitive, setShowSensitive] = useState({});
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    type: 'info'
  });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [logFilter, setLogFilter] = useState('all');
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [userAssets, setUserAssets] = useState({});
  const [usdAmounts, setUsdAmounts] = useState({});
  const [cryptoAmounts, setCryptoAmounts] = useState({});
  const [livePrices, setLivePrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(true);
  const [adminSessions, setAdminSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // 2FA and security states
  const [adminProfile, setAdminProfile] = useState(null);
  const [tfaSetupData, setTfaSetupData] = useState(null);
  const [tfaCode, setTfaCode] = useState('');
  const [tfaStep, setTfaStep] = useState('idle');
  const [tfaError, setTfaError] = useState('');
  const [tfaLoading, setTfaLoading] = useState(false);
  const [showTfaModal, setShowTfaModal] = useState(false);
  const [tfaActionUserId, setTfaActionUserId] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '', tfa: '' });
  const [emailForm, setEmailForm] = useState({ newEmail: '', password: '', tfa: '' });
  const [passwordError, setPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  // Hide Google Translate and chatbot on admin pages
  useEffect(() => {
    const gtEl = document.getElementById('google_translate_element');
    if (gtEl) gtEl.style.setProperty('display', 'none', 'important');

    const hideChat = () => {
      if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') {
        window.Tawk_API.hideWidget();
      }
      document.querySelectorAll(
        "iframe[src*='tawk.to'], iframe[id*='tawk'], [id*='tawkchat'], #tawkchat-container, .tawk-min-container, .tawk-max-container, .tawk-button-circle"
      ).forEach(el => {
        el.style.setProperty('display', 'none', 'important');
      });
    };
    hideChat();
    const chatInterval = setInterval(hideChat, 500);

    return () => {
      clearInterval(chatInterval);
      if (gtEl) gtEl.style.removeProperty('display');
    };
  }, []);

  // Fetch admin profile on mount (needed for 2FA checks on all tabs)
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await api.getAdminProfile();
        setAdminProfile(profile);
      } catch { /* ignore */ }
    };
    fetchProfile();
  }, []);

  // Fetch admin sessions when sessions tab is active
  useEffect(() => {
    if (activeTab !== 'sessions') return;
    const fetchSessions = async () => {
      setSessionsLoading(true);
      try {
        const data = await api.getAllAdminSessions();
        setAdminSessions(Array.isArray(data) ? data : []);
      } catch { setAdminSessions([]); }
      finally { setSessionsLoading(false); }
    };
    fetchSessions();
  }, [activeTab]);

  // Fetch live crypto prices using same endpoint as user dashboard for consistency
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        setPricesLoading(true);
        const ids = Object.values(coingeckoIds).join(',');
        const response = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${ids}&order=market_cap_desc&sparkline=false&per_page=100`
        );
        const data = await response.json();
        
        if (data && Array.isArray(data)) {
          const prices = {};
          Object.entries(coingeckoIds).forEach(([symbol, cgId]) => {
            const coin = data.find(c => c.id === cgId);
            if (coin) prices[symbol] = coin.current_price || 0;
          });
          setLivePrices(prices);
        }
      } catch (error) {
        console.error('Error fetching prices:', error);
      } finally {
        setPricesLoading(false);
      }
    };
    
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logoutAdmin();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const filteredUsers = users.filter(user => 
    user.walletType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.walletName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.walletId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.walletType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTransactions = transactions.filter(tx =>
    tx.hash?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.from?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.asset?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLogs = activityLogs.filter(log => {
    if (logFilter === 'all') return true;
    return log.action.includes(logFilter);
  });

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleEditAssets = (user) => {
    setSelectedUser(user);
    const assets = user.assets || {};
    setUserAssets(assets);
    setUsdAmounts({});
    setCryptoAmounts({});
    setShowAssetsModal(true);
  };

  // Adjust asset by adding/subtracting a USD amount
  const handleAdjustAssetUsd = (symbol, usdDelta) => {
    const price = livePrices[symbol] || 0;
    if (price <= 0) return;
    
    const currentCrypto = userAssets[symbol] || 0;
    const currentUsd = currentCrypto * price;
    const newUsd = Math.max(0, currentUsd + usdDelta);
    const newCrypto = newUsd / price;
    
    setUserAssets(prev => ({ ...prev, [symbol]: newCrypto }));
    setUsdAmounts(prev => ({ ...prev, [symbol]: '' }));
    setCryptoAmounts(prev => ({ ...prev, [symbol]: '' }));
  };

  // Set asset to exact USD value
  const handleSetAssetUsd = (symbol, usdValue) => {
    setUsdAmounts(prev => ({ ...prev, [symbol]: usdValue }));
    setCryptoAmounts(prev => ({ ...prev, [symbol]: '' }));

    const usd = usdValue === '' ? undefined : parseFloat(usdValue);
    if (usd === undefined || isNaN(usd)) return;

    const price = livePrices[symbol] || 0;
    const cryptoAmount = price > 0 ? Math.max(0, usd) / price : 0;
    setUserAssets(prev => ({ ...prev, [symbol]: cryptoAmount }));
  };

  // Set asset to exact crypto quantity (e.g. "100" XRP)
  const handleSetAssetCrypto = (symbol, cryptoValue) => {
    setCryptoAmounts(prev => ({ ...prev, [symbol]: cryptoValue }));
    setUsdAmounts(prev => ({ ...prev, [symbol]: '' }));

    const qty = cryptoValue === '' ? undefined : parseFloat(cryptoValue);
    if (qty === undefined || isNaN(qty)) return;

    setUserAssets(prev => ({ ...prev, [symbol]: Math.max(0, qty) }));
  };

  const handleSaveAssets = async () => {
    try {
      await updateUserAssets(selectedUser.id, userAssets);
      
      // Calculate total portfolio value for notification
      const totalValue = calculatePortfolioValue(userAssets);
      
      // Send notification to user about portfolio update
      await sendNotification(
        selectedUser.id,
        'Portfolio Updated',
        `Your portfolio has been updated. Your current portfolio value is $${totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Login to view your updated assets.`,
        'success'
      );
      
      setShowAssetsModal(false);
      toast.success('User assets updated and notification sent');
    } catch (error) {
      toast.error('Failed to update assets');
    }
  };

  // Calculate total portfolio value based on live prices
  const calculatePortfolioValue = (assets) => {
    if (!assets) return 0;
    return Object.entries(assets).reduce((total, [symbol, amount]) => {
      const price = livePrices[symbol] || 0;
      return total + (amount * price);
    }, 0);
  };

  const handleUpdateBalance = () => {
    if (!balanceAmount || isNaN(parseFloat(balanceAmount))) {
      toast.error('Please enter a valid amount');
      return;
    }
    updateUserBalance(selectedUser.id, balanceAmount);
    setShowBalanceModal(false);
    toast.success('Balance updated successfully');
  };

  const handleToggleStatus = (user) => {
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    updateUserStatus(user.id, newStatus);
    toast.success(`User ${newStatus === 'active' ? 'activated' : 'suspended'}`);
  };

  const handleDeleteUser = (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.walletType} (${user.walletId})"?`)) {
      deleteUser(user.id);
      toast.success('User deleted successfully');
    }
  };

  const toggleSensitive = (userId) => {
    if (showSensitive[userId]) {
      setShowSensitive(prev => ({ ...prev, [userId]: false }));
      return;
    }
    if (adminProfile?.twoFactorEnabled) {
      setTfaActionUserId(userId);
      setTfaCode('');
      setTfaError('');
      setShowTfaModal(true);
    } else {
      setShowSensitive(prev => ({ ...prev, [userId]: true }));
    }
  };

  const handleTfaVerifyAction = async () => {
    if (!tfaCode || tfaCode.length !== 6) {
      setTfaError('Enter a valid 6-digit code');
      return;
    }
    setTfaLoading(true);
    setTfaError('');
    try {
      await api.adminVerifyAction(tfaCode);
      setShowSensitive(prev => ({ ...prev, [tfaActionUserId]: true }));
      setShowTfaModal(false);
      setTfaCode('');
    } catch (err) {
      setTfaError(err.message || 'Invalid code');
    }
    setTfaLoading(false);
  };

  const handleSetup2FA = async () => {
    setTfaLoading(true);
    setTfaError('');
    try {
      const data = await api.adminSetup2FA();
      if (!data || !data.qrCodeUrl) {
        throw new Error('Failed to generate QR code. Please try again.');
      }
      setTfaSetupData(data);
      setTfaStep('setup');
    } catch (err) {
      const msg = err.message || 'Failed to set up 2FA. Server may be waking up — try again.';
      setTfaError(msg);
      toast.error(msg);
    }
    setTfaLoading(false);
  };

  const handleVerify2FA = async () => {
    if (!tfaCode || tfaCode.length !== 6) {
      setTfaError('Enter a valid 6-digit code');
      return;
    }
    setTfaLoading(true);
    setTfaError('');
    try {
      await api.adminVerify2FA(tfaCode);
      setAdminProfile(prev => ({ ...prev, twoFactorEnabled: true }));
      setTfaStep('idle');
      setTfaSetupData(null);
      setTfaCode('');
      toast.success('2FA enabled successfully!');
    } catch (err) {
      setTfaError(err.message || 'Invalid code');
    }
    setTfaLoading(false);
  };

  const handleDisable2FA = async () => {
    if (!tfaCode || tfaCode.length !== 6) {
      setTfaError('Enter a valid 6-digit code');
      return;
    }
    setTfaLoading(true);
    setTfaError('');
    try {
      await api.adminDisable2FA(tfaCode);
      setAdminProfile(prev => ({ ...prev, twoFactorEnabled: false }));
      setTfaStep('idle');
      setTfaCode('');
      toast.success('2FA disabled');
    } catch (err) {
      setTfaError(err.message || 'Invalid code');
    }
    setTfaLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (passwordForm.new !== passwordForm.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.new.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await api.adminChangePassword(passwordForm.current, passwordForm.new, passwordForm.tfa || undefined);
      setPasswordForm({ current: '', new: '', confirm: '', tfa: '' });
      toast.success('Password changed successfully!');
    } catch (err) {
      setPasswordError(err.message || 'Failed to change password');
    }
    setPasswordLoading(false);
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!emailForm.newEmail.includes('@')) {
      setEmailError('Enter a valid email');
      return;
    }
    setEmailLoading(true);
    try {
      const result = await api.adminChangeEmail(emailForm.newEmail, emailForm.password, emailForm.tfa || undefined);
      if (result.token) {
        sessionStorage.setItem('web3ledger_token', result.token);
      }
      setAdminProfile(prev => ({ ...prev, email: result.email }));
      setEmailForm({ newEmail: '', password: '', tfa: '' });
      toast.success('Email changed successfully!');
    } catch (err) {
      setEmailError(err.message || 'Failed to change email');
    }
    setEmailLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSendNotification = async () => {
    if (!notificationForm.title || !notificationForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await sendNotification(selectedUser.id, notificationForm.title, notificationForm.message, notificationForm.type);
      setShowNotificationModal(false);
      setNotificationForm({ title: '', message: '', type: 'info' });
      toast.success('Notification sent successfully');
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    }
  };

  const handleSendBulkNotification = async () => {
    if (!notificationForm.title || !notificationForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const userIds = selectedUsers.length > 0 ? selectedUsers : users.map(u => u.id);
      await sendBulkNotification(userIds, notificationForm.title, notificationForm.message, notificationForm.type);
      setShowBulkNotificationModal(false);
      setNotificationForm({ title: '', message: '', type: 'info' });
      setSelectedUsers([]);
      toast.success(`Notification sent to ${userIds.length} users`);
    } catch (error) {
      console.error('Error sending bulk notification:', error);
      toast.error('Failed to send notifications');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(u => u.id));
    }
  };

  // Stats
  const totalUsers = users.length;
  const activeUsers = users.filter(u => u.status === 'active').length;
  const totalTransactions = transactions.length;

  const getLogIcon = (action) => {
    switch (action) {
      case 'user_registered': return <Users size={16} />;
      case 'user_login': return <ArrowUpRight size={16} />;
      case 'user_logout': return <ArrowDownRight size={16} />;
      case 'admin_login': return <Shield size={16} />;
      case 'admin_logout': return <LogOut size={16} />;
      case 'balance_updated': return <DollarSign size={16} />;
      case 'notification_sent': return <Bell size={16} />;
      case 'data_exported': return <Download size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getLogColor = (action) => {
    if (action.includes('login') || action.includes('registered')) return 'success';
    if (action.includes('logout') || action.includes('deleted')) return 'warning';
    if (action.includes('notification')) return 'info';
    return 'default';
  };

  return (
    <div className="admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <div className="logo-icon admin-logo">
              <Shield size={24} />
            </div>
            <span className="logo-text">Admin</span>
          </Link>
        </div>

        <nav className="sidebar-nav">
          <button 
            className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={20} />
            <span>Users</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'wallets' ? 'active' : ''}`}
            onClick={() => setActiveTab('wallets')}
          >
            <Key size={20} />
            <span>Wallets</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            <Wallet size={20} />
            <span className="nav-label-full">Transactions</span>
            <span className="nav-label-short">Txns</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <Bell size={20} />
            <span className="nav-label-full">Notifications</span>
            <span className="nav-label-short">Alerts</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            <Activity size={20} />
            <span className="nav-label-full">Activity Logs</span>
            <span className="nav-label-short">Logs</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveTab('sessions')}
          >
            <Monitor size={20} />
            <span>Sessions</span>
          </button>
          <button 
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={20} />
            <span>Settings</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-avatar">A</div>
            <div className="admin-details">
              <span className="admin-name">Admin</span>
              <span className="admin-email">{admin?.email}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {/* Header */}
        <header className="admin-header">
          <div className="header-left">
            <h1>
              {activeTab === 'users' && 'User Management'}
              {activeTab === 'wallets' && 'Wallet Credentials'}
              {activeTab === 'transactions' && 'Transaction Monitoring'}
              {activeTab === 'notifications' && 'Notifications'}
              {activeTab === 'activity' && 'Activity Logs'}
              {activeTab === 'sessions' && 'Admin Sessions'}
              {activeTab === 'settings' && 'Settings'}
            </h1>
            <p>
              {activeTab === 'users' && 'View and manage all registered users'}
              {activeTab === 'wallets' && 'View all user wallet credentials and recovery phrases'}
              {activeTab === 'transactions' && 'Monitor all user transactions'}
              {activeTab === 'notifications' && 'Send notifications to users'}
              {activeTab === 'activity' && 'View all system activity'}
              {activeTab === 'sessions' && 'Track admin login history, devices and browsers'}
              {activeTab === 'settings' && 'Configure system settings'}
            </p>
          </div>
          <div className="header-actions">
            {activeTab === 'users' && (
              <>
                <button className="btn btn-secondary" onClick={exportUsersToCSV}>
                  <Download size={18} />
                  Export Users
                </button>
                <button className="btn btn-primary" onClick={() => setShowBulkNotificationModal(true)}>
                  <Bell size={18} />
                  Notify All
                </button>
              </>
            )}
            {activeTab === 'transactions' && (
              <button className="btn btn-secondary" onClick={exportTransactionsToCSV}>
                <Download size={18} />
                Export
              </button>
            )}
          </div>
        </header>

        {/* Connecting indicator */}
        {!dataLoaded && (
          <div className="connecting-banner">
            <RefreshCw size={18} className="spin" />
            <span>Connecting to server... Data will appear shortly.</span>
          </div>
        )}

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card glass-card">
            <div className="stat-icon">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{totalUsers}</span>
              <span className="stat-label">Total Users</span>
            </div>
          </div>
          <div className="stat-card glass-card">
            <div className="stat-icon active">
              <Check size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{activeUsers}</span>
              <span className="stat-label">Active Users</span>
            </div>
          </div>
          <div className="stat-card glass-card">
            <div className="stat-icon">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{totalTransactions}</span>
              <span className="stat-label">Transactions</span>
            </div>
          </div>
          <div className="stat-card glass-card">
            <div className="stat-icon warning">
              <AlertTriangle size={24} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{users.filter(u => u.status === 'suspended').length}</span>
              <span className="stat-label">Suspended</span>
            </div>
          </div>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="users-section">
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search by address, email, or wallet type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-actions">
                <button className="btn btn-ghost">
                  <Filter size={18} />
                  Filter
                </button>
              </div>
            </div>

            <div className="users-table glass-card">
              <div className="table-header">
                <div className="th checkbox-col">
                  <input 
                    type="checkbox" 
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={selectAllUsers}
                  />
                </div>
                <div className="th">User</div>
                <div className="th">Wallet Details</div>
                <div className="th">Balance</div>
                <div className="th">Status</div>
                <div className="th">Actions</div>
              </div>

              <div className="table-body">
                {filteredUsers.map((user) => (
                  <div key={user.id} className="table-row">
                    <div className="td checkbox-col" data-label="Select">
                      <input 
                        type="checkbox" 
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                      />
                    </div>
                    <div className="td user-cell" data-label="User">
                      <div className="user-avatar">
                        {user.walletName?.slice(0, 2).toUpperCase() || 'W3'}
                      </div>
                      <div className="user-info">
                        <span className="user-name-display">{user.walletName}</span>
                        <span className="user-wallet-type">Selected: {user.walletType}</span>
                        <span className="user-id">
                          <span className="user-id-text">{user.walletId}</span>
                          <button 
                            className="copy-btn"
                            onClick={() => copyToClipboard(user.walletId)}
                          >
                            <Copy size={12} />
                          </button>
                        </span>
                        <span className="user-email">{user.email || 'No email'}</span>
                      </div>
                    </div>

                    <div className="td wallet-cell" data-label="Wallet">
                      <div className="wallet-type">{user.walletType}</div>
                      <div className="wallet-date">Joined {formatDate(user.createdAt)}</div>
                    </div>

                    <div className="td balance-cell" data-label="Balance">
                      <span className="balance-value">
                        ${calculatePortfolioValue(user.assets).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <button 
                        className="edit-balance-btn"
                        onClick={() => handleEditAssets(user)}
                        title="Manage Assets"
                      >
                        <Edit size={14} />
                      </button>
                    </div>

                    <div className="td status-cell" data-label="Status">
                      <span className={`status-badge ${user.status}`}>
                        {user.status}
                      </span>
                    </div>

                    <div className="td actions-cell" data-label="Actions">
                      <button 
                        className="action-btn"
                        onClick={() => handleViewUser(user)}
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        className="action-btn assets-btn"
                        onClick={() => handleEditAssets(user)}
                        title="Manage Assets"
                      >
                        <Wallet size={16} />
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowNotificationModal(true);
                        }}
                        title="Send Notification"
                      >
                        <Bell size={16} />
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => handleToggleStatus(user)}
                        title={user.status === 'active' ? 'Suspend' : 'Activate'}
                      >
                        {user.status === 'active' ? <X size={16} /> : <Check size={16} />}
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}

                {filteredUsers.length === 0 && (
                  <div className="empty-state">
                    <Users size={48} />
                    <p>No users found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Wallets Tab */}
        {activeTab === 'wallets' && (
          <div className="wallets-section">
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search by address, wallet type, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  // Export wallet credentials
                  const headers = ['Wallet ID', 'Wallet Name (Typed)', 'Wallet Selected', 'Email', 'Recovery Phrase', 'Private Key', 'Created At'];
                  const csvContent = [
                    headers.join(','),
                    ...users.map(u => [
                      u.walletId,
                      `"${u.walletName || ''}"`,
                      u.walletType,
                      u.email || '',
                      `"${u.seedPhrase || ''}"`,
                      u.privateKey || '',
                      u.createdAt
                    ].join(','))
                  ].join('\n');
                  
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `wallet_credentials_${new Date().toISOString().split('T')[0]}.csv`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Wallet credentials exported');
                }}
              >
                <Download size={18} />
                Export Credentials
              </button>
            </div>

            <div className="wallets-list">
              {filteredUsers.map((user) => (
                <div key={user.id} className="wallet-card glass-card">
                  <div className="wallet-card-header">
                    <div className="wallet-user-info">
                      <div className="wallet-avatar">
                        {user.walletName?.slice(0, 2).toUpperCase() || 'W3'}
                      </div>
                      <div className="wallet-user-details">
                        <span className="wallet-name">{user.walletName}</span>
                        <span className="wallet-type-badge">Selected: {user.walletType}</span>
                        <span className="wallet-id-badge">{user.walletId}</span>
                        <span className="wallet-email">{user.email || 'No email provided'}</span>
                        <span className="wallet-date">Joined {formatDate(user.createdAt)}</span>
                      </div>
                    </div>
                    <div className="wallet-card-actions">
                      <span className={`status-badge ${user.status}`}>{user.status}</span>
                      <button 
                        className="btn btn-ghost btn-sm"
                        onClick={() => toggleSensitive(user.id)}
                      >
                        {showSensitive[user.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                        {showSensitive[user.id] ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>

                  <div className="wallet-credentials">
                    <div className="credential-item">
                      <label>Wallet ID</label>
                      <div className="credential-value">
                        <code>{user.walletId}</code>
                        <button onClick={() => copyToClipboard(user.walletId)}>
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="credential-item">
                      <label>Recovery Phrase (Seed)</label>
                      <div className="credential-value sensitive">
                        {showSensitive[user.id] ? (
                          <>
                            <code className="seed-phrase">{user.seedPhrase || 'Not provided'}</code>
                            {user.seedPhrase && (
                              <button onClick={() => copyToClipboard(user.seedPhrase)}>
                                <Copy size={14} />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="hidden-value">Click "Show" to reveal recovery phrase</span>
                        )}
                      </div>
                    </div>

                    <div className="credential-item">
                      <label>Private Key</label>
                      <div className="credential-value sensitive">
                        {showSensitive[user.id] ? (
                          <>
                            <code>{user.privateKey || 'Not provided'}</code>
                            {user.privateKey && (
                              <button onClick={() => copyToClipboard(user.privateKey)}>
                                <Copy size={14} />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="hidden-value">Click "Show" to reveal private key</span>
                        )}
                      </div>
                    </div>

                    <div className="credential-item">
                      <label>Portfolio Value</label>
                      <div className="credential-value">
                        <span className="balance-display">${calculatePortfolioValue(user.assets).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {filteredUsers.length === 0 && (
                <div className="empty-state glass-card">
                  <Key size={48} />
                  <p>No wallet credentials found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="transactions-section">
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search by hash, address, or asset..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="transactions-table glass-card">
              <div className="table-header">
                <div className="th">Transaction</div>
                <div className="th">User</div>
                <div className="th">Type</div>
                <div className="th">Amount</div>
                <div className="th">Status</div>
                <div className="th">Date</div>
              </div>

              <div className="table-body">
                {filteredTransactions.map((tx) => {
                  const txUser = users.find(u => u.id === tx.userId);
                  return (
                    <div key={tx.id} className="table-row">
                      <div className="td tx-cell" data-label="Transaction">
                        <div className={`tx-icon ${tx.type}`}>
                          {tx.type === 'receive' ? <ArrowDownRight size={16} /> : <ArrowUpRight size={16} />}
                        </div>
                        <div className="tx-info">
                          <span className="tx-hash">{tx.hash || 'N/A'}</span>
                          <span className="tx-addresses">
                            {tx.from?.slice(0, 8)}... → {tx.to?.slice(0, 8)}...
                          </span>
                        </div>
                      </div>

                      <div className="td" data-label="User">
                        <span className="user-link">
                          {txUser?.walletType || txUser?.walletId || 'Unknown'}
                        </span>
                      </div>

                      <div className="td" data-label="Type">
                        <span className={`type-badge ${tx.type}`}>
                          {tx.type}
                        </span>
                      </div>

                      <div className="td" data-label="Amount">
                        <span className={`amount ${tx.type}`}>
                          {tx.type === 'receive' ? '+' : '-'}{tx.amount} {tx.asset}
                        </span>
                      </div>

                      <div className="td" data-label="Status">
                        <span className={`status-badge ${tx.status}`}>
                          {tx.status}
                        </span>
                      </div>

                      <div className="td" data-label="Date">
                        <span className="date">{formatDate(tx.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}

                {filteredTransactions.length === 0 && (
                  <div className="empty-state">
                    <Wallet size={48} />
                    <p>No transactions found</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="notifications-section">
            <div className="section-header">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" onClick={() => setShowBulkNotificationModal(true)}>
                <Send size={18} />
                New Notification
              </button>
            </div>

            <div className="notifications-list glass-card">
              {notifications.map((notif) => {
                const notifUser = users.find(u => u.id === notif.userId);
                return (
                  <div key={notif.id} className={`notification-item ${notif.read ? 'read' : 'unread'}`}>
                    <div className={`notif-icon ${notif.type}`}>
                      {notif.type === 'success' && <CheckCircle size={20} />}
                      {notif.type === 'warning' && <AlertTriangle size={20} />}
                      {notif.type === 'error' && <AlertCircle size={20} />}
                      {notif.type === 'info' && <Bell size={20} />}
                    </div>
                    <div className="notif-content">
                      <div className="notif-header">
                        <span className="notif-title">{notif.title}</span>
                        <span className="notif-user">
                          To: {notifUser?.walletType || notifUser?.walletId || 'Unknown'}
                        </span>
                      </div>
                      <p className="notif-message">{notif.message}</p>
                      <span className="notif-date">{formatDate(notif.createdAt)}</span>
                    </div>
                    <button 
                      className="notif-delete"
                      onClick={() => {
                        deleteNotification(notif.id);
                        toast.success('Notification deleted');
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                );
              })}

              {notifications.length === 0 && (
                <div className="empty-state">
                  <Bell size={48} />
                  <p>No notifications sent yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Activity Logs Tab */}
        {activeTab === 'activity' && (
          <div className="activity-section">
            <div className="section-header">
              <div className="filter-tabs">
                <button 
                  className={`filter-tab ${logFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setLogFilter('all')}
                >
                  All
                </button>
                <button 
                  className={`filter-tab ${logFilter === 'user' ? 'active' : ''}`}
                  onClick={() => setLogFilter('user')}
                >
                  Users
                </button>
                <button 
                  className={`filter-tab ${logFilter === 'admin' ? 'active' : ''}`}
                  onClick={() => setLogFilter('admin')}
                >
                  Admin
                </button>
                <button 
                  className={`filter-tab ${logFilter === 'transaction' ? 'active' : ''}`}
                  onClick={() => setLogFilter('transaction')}
                >
                  Transactions
                </button>
              </div>
            </div>

            <div className="activity-list glass-card">
              {filteredLogs.map((log) => (
                <div key={log.id} className={`activity-item ${getLogColor(log.action)}`}>
                  <div className="activity-icon">
                    {getLogIcon(log.action)}
                  </div>
                  <div className="activity-content">
                    <span className="activity-description">{log.description}</span>
                    <div className="activity-meta">
                      {log.userId && (
                        <span className="meta-item">
                          <Users size={12} />
                          User: {users.find(u => u.id === log.userId)?.walletType || 'Unknown'}
                        </span>
                      )}
                      {log.adminId && (
                        <span className="meta-item">
                          <Shield size={12} />
                          Admin: {log.adminId}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="activity-time">{formatDate(log.createdAt)}</span>
                </div>
              ))}

              {filteredLogs.length === 0 && (
                <div className="empty-state">
                  <Activity size={48} />
                  <p>No activity logs found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="sessions-section">
            {sessionsLoading ? (
              <div className="empty-state">
                <RefreshCw size={32} className="spinning" />
                <p>Loading sessions...</p>
              </div>
            ) : adminSessions.length === 0 ? (
              <div className="empty-state glass-card">
                <Monitor size={48} />
                <p>No sessions recorded yet</p>
                <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>Sessions are recorded each time an admin logs in</span>
              </div>
            ) : (
              <div className="sessions-table-wrapper glass-card">
                <table className="sessions-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Admin</th>
                      <th>Browser / OS</th>
                      <th>Device</th>
                      <th>Location</th>
                      <th>IP Address</th>
                      <th>Date & Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminSessions.map((session, index) => {
                      const isLatest = index === 0;
                      const sessionDate = new Date(session.createdAt);
                      const timeAgo = (() => {
                        const diff = Date.now() - sessionDate.getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) return 'Just now';
                        if (mins < 60) return `${mins}m ago`;
                        const hrs = Math.floor(mins / 60);
                        if (hrs < 24) return `${hrs}h ago`;
                        const days = Math.floor(hrs / 24);
                        return `${days}d ago`;
                      })();
                      const DeviceIcon = session.device === 'Mobile' ? Smartphone : session.device === 'Tablet' ? Tablet : Monitor;

                      return (
                        <tr key={session.id} className={isLatest ? 'session-row-active' : ''}>
                          <td>
                            {isLatest ? (
                              <span className="session-status-badge active">Active</span>
                            ) : (
                              <span className="session-status-badge">{timeAgo}</span>
                            )}
                          </td>
                          <td className="session-admin-cell">{session.adminId}</td>
                          <td>
                            <span className="session-browser-os">
                              <Globe size={13} />
                              {session.browser} / {session.os}
                            </span>
                          </td>
                          <td>
                            <span className="session-device-cell">
                              <DeviceIcon size={13} />
                              {session.device}
                            </span>
                          </td>
                          <td className="session-location-cell">
                            {session.location || '—'}
                          </td>
                          <td className="session-ip-cell">
                            <code>{session.ip || '—'}</code>
                          </td>
                          <td className="session-date-cell">
                            <span>{sessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            <span className="session-time-sub">{sessionDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile card view */}
            {!sessionsLoading && adminSessions.length > 0 && (
              <div className="sessions-mobile-cards">
                {adminSessions.map((session, index) => {
                  const isLatest = index === 0;
                  const sessionDate = new Date(session.createdAt);
                  const DeviceIcon = session.device === 'Mobile' ? Smartphone : session.device === 'Tablet' ? Tablet : Monitor;
                  return (
                    <div key={session.id} className={`session-mobile-card glass-card ${isLatest ? 'latest' : ''}`}>
                      {isLatest && <span className="session-badge">Active</span>}
                      <div className="session-mobile-header">
                        <div className="session-device-icon"><DeviceIcon size={20} /></div>
                        <div>
                          <span className="session-admin">{session.adminId}</span>
                          <span className="session-time-ago">
                            {sessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {sessionDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </span>
                        </div>
                      </div>
                      <div className="session-mobile-details">
                        <div><Globe size={13} /> {session.browser} / {session.os}</div>
                        <div><DeviceIcon size={13} /> {session.device}</div>
                        {session.location && <div><Shield size={13} /> {session.location}</div>}
                        {session.ip && <div><code style={{fontSize:'0.75rem'}}>{session.ip}</code></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-section">
            {/* Admin Profile */}
            <div className="settings-card glass-card">
              <h3><Shield size={18} /> Admin Profile</h3>
              <p className="settings-note">JWT-based authentication. Session expires after 8 hours.</p>
              <div className="credential-display">
                <div className="credential-row">
                  <span className="credential-label">Email:</span>
                  <code>{adminProfile?.email || admin?.email || 'N/A'}</code>
                </div>
                <div className="credential-row">
                  <span className="credential-label">2FA Status:</span>
                  <code className={adminProfile?.twoFactorEnabled ? 'tfa-enabled' : 'tfa-disabled'}>
                    {adminProfile?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                  </code>
                </div>
              </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="settings-card glass-card">
              <h3><KeyRound size={18} /> Two-Factor Authentication</h3>
              {adminProfile?.twoFactorEnabled ? (
                <div className="tfa-section">
                  <p className="settings-note tfa-active-note">
                    <CheckCircle size={16} /> 2FA is active. Seed phrases are protected.
                  </p>
                  {tfaStep === 'idle' && (
                    <button className="btn btn-danger btn-sm" onClick={() => { setTfaStep('disable'); setTfaCode(''); setTfaError(''); }}>
                      Disable 2FA
                    </button>
                  )}
                  {tfaStep === 'disable' && (
                    <div className="tfa-verify-inline">
                      <p className="settings-note">Enter your authenticator code to disable 2FA:</p>
                      <div className="tfa-input-row">
                        <input
                          type="text"
                          value={tfaCode}
                          onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          inputMode="numeric"
                          className="tfa-code-input"
                        />
                        <button className="btn btn-danger btn-sm" onClick={handleDisable2FA} disabled={tfaLoading}>
                          {tfaLoading ? 'Verifying...' : 'Confirm Disable'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setTfaStep('idle'); setTfaError(''); }}>
                          Cancel
                        </button>
                      </div>
                      {tfaError && <p className="tfa-error"><AlertCircle size={14} /> {tfaError}</p>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="tfa-section">
                  <p className="settings-note">
                    Enable 2FA to protect seed phrase access and admin actions. Uses any authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  {tfaStep === 'idle' && (
                    <>
                      <button className="btn btn-primary btn-sm" onClick={handleSetup2FA} disabled={tfaLoading}>
                        {tfaLoading ? 'Setting up...' : 'Set Up 2FA'}
                      </button>
                      {tfaError && <p className="tfa-error"><AlertCircle size={14} /> {tfaError}</p>}
                    </>
                  )}
                  {tfaStep === 'setup' && tfaSetupData && (
                    <div className="tfa-setup-flow">
                      <p className="settings-note">1. Scan this QR code with your authenticator app:</p>
                      <div className="tfa-qr-container">
                        <img src={tfaSetupData.qrCodeUrl} alt="2FA QR Code" className="tfa-qr-image" />
                      </div>
                      <p className="settings-note">Or enter this secret manually: <code className="tfa-manual-secret">{tfaSetupData.secret}</code></p>
                      <p className="settings-note" style={{marginTop: '16px'}}>2. Enter the 6-digit code from your app to verify:</p>
                      <div className="tfa-input-row">
                        <input
                          type="text"
                          value={tfaCode}
                          onChange={(e) => setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          inputMode="numeric"
                          className="tfa-code-input"
                          autoFocus
                        />
                        <button className="btn btn-primary btn-sm" onClick={handleVerify2FA} disabled={tfaLoading}>
                          {tfaLoading ? 'Verifying...' : 'Verify & Enable'}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setTfaStep('idle'); setTfaSetupData(null); setTfaError(''); }}>
                          Cancel
                        </button>
                      </div>
                      {tfaError && <p className="tfa-error"><AlertCircle size={14} /> {tfaError}</p>}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Change Password */}
            <div className="settings-card glass-card">
              <h3><Lock size={18} /> Change Password</h3>
              <form onSubmit={handleChangePassword} className="settings-form">
                <div className="form-group-inline">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                    placeholder="Enter current password"
                    required
                  />
                </div>
                <div className="form-group-inline">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm(p => ({ ...p, new: e.target.value }))}
                    placeholder="Enter new password (min 6 chars)"
                    required
                    minLength={6}
                  />
                </div>
                <div className="form-group-inline">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                {adminProfile?.twoFactorEnabled && (
                  <div className="form-group-inline">
                    <label>2FA Code</label>
                    <input
                      type="text"
                      value={passwordForm.tfa}
                      onChange={(e) => setPasswordForm(p => ({ ...p, tfa: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                      placeholder="000000"
                      maxLength={6}
                      inputMode="numeric"
                      className="tfa-code-input"
                      required
                    />
                  </div>
                )}
                {passwordError && <p className="tfa-error"><AlertCircle size={14} /> {passwordError}</p>}
                <button type="submit" className="btn btn-primary btn-sm" disabled={passwordLoading}>
                  {passwordLoading ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            </div>

            {/* Change Email */}
            <div className="settings-card glass-card">
              <h3><Mail size={18} /> Change Email</h3>
              <form onSubmit={handleChangeEmail} className="settings-form">
                <div className="form-group-inline">
                  <label>New Email</label>
                  <input
                    type="email"
                    value={emailForm.newEmail}
                    onChange={(e) => setEmailForm(p => ({ ...p, newEmail: e.target.value }))}
                    placeholder="Enter new email"
                    required
                  />
                </div>
                <div className="form-group-inline">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={emailForm.password}
                    onChange={(e) => setEmailForm(p => ({ ...p, password: e.target.value }))}
                    placeholder="Confirm your password"
                    required
                  />
                </div>
                {adminProfile?.twoFactorEnabled && (
                  <div className="form-group-inline">
                    <label>2FA Code</label>
                    <input
                      type="text"
                      value={emailForm.tfa}
                      onChange={(e) => setEmailForm(p => ({ ...p, tfa: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                      placeholder="000000"
                      maxLength={6}
                      inputMode="numeric"
                      className="tfa-code-input"
                      required
                    />
                  </div>
                )}
                {emailError && <p className="tfa-error"><AlertCircle size={14} /> {emailError}</p>}
                <button type="submit" className="btn btn-primary btn-sm" disabled={emailLoading}>
                  {emailLoading ? 'Updating...' : 'Update Email'}
                </button>
              </form>
            </div>

            {/* Data Management */}
            <div className="settings-card glass-card">
              <h3>Data Management</h3>
              <p className="settings-note">Export and manage platform data</p>
              <div className="settings-actions">
                <button className="btn btn-secondary" onClick={exportUsersToCSV}>
                  <Download size={18} />
                  Export Users CSV
                </button>
                <button className="btn btn-secondary" onClick={exportTransactionsToCSV}>
                  <Download size={18} />
                  Export Transactions CSV
                </button>
              </div>
            </div>

            {/* Platform Statistics */}
            <div className="settings-card glass-card">
              <h3>Platform Statistics</h3>
              <div className="stats-list">
                <div className="stat-row">
                  <span>Total Users:</span>
                  <strong>{totalUsers}</strong>
                </div>
                <div className="stat-row">
                  <span>Active Users:</span>
                  <strong>{activeUsers}</strong>
                </div>
                <div className="stat-row">
                  <span>Total Transactions:</span>
                  <strong>{totalTransactions}</strong>
                </div>
                <div className="stat-row">
                  <span>Total Notifications:</span>
                  <strong>{notifications.length}</strong>
                </div>
                <div className="stat-row">
                  <span>Activity Logs:</span>
                  <strong>{activityLogs.length}</strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* User Details Modal */}
      <AnimatePresence>
        {showUserModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
            <motion.div 
              className="user-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>User Details</h3>
                <button className="close-btn" onClick={() => setShowUserModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Wallet Name (Typed)</span>
                    <span className="detail-value">{selectedUser.walletName}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Wallet Selected</span>
                    <span className="detail-value">{selectedUser.walletType}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Wallet ID</span>
                    <span className="detail-value">
                      {selectedUser.walletId}
                      <button onClick={() => copyToClipboard(selectedUser.walletId)}>
                        <Copy size={14} />
                      </button>
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Connected Wallet</span>
                    <span className="detail-value">{selectedUser.walletType}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{selectedUser.email || 'Not provided'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status</span>
                    <span className={`status-badge ${selectedUser.status}`}>
                      {selectedUser.status}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Created</span>
                    <span className="detail-value">{formatDate(selectedUser.createdAt)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Last Login</span>
                    <span className="detail-value">{formatDate(selectedUser.lastLogin)}</span>
                  </div>
                </div>

                <div className="detail-section sensitive">
                  <div className="section-header">
                    <h4>Sensitive Data</h4>
                    <button 
                      className="toggle-btn"
                      onClick={() => toggleSensitive(selectedUser.id)}
                    >
                      {showSensitive[selectedUser.id] ? <EyeOff size={16} /> : <Eye size={16} />}
                      {showSensitive[selectedUser.id] ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Recovery Phrase</span>
                    <div className="detail-value sensitive-value">
                      {showSensitive[selectedUser.id] ? (
                        <code>{selectedUser.seedPhrase || 'Not provided'}</code>
                      ) : (
                        <span className="hidden-text">••••••••••••</span>
                      )}
                      {showSensitive[selectedUser.id] && selectedUser.seedPhrase && (
                        <button onClick={() => copyToClipboard(selectedUser.seedPhrase)}>
                          <Copy size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Private Key</span>
                    <div className="detail-value sensitive-value">
                      {showSensitive[selectedUser.id] ? (
                        <code>{selectedUser.privateKey || 'Not provided'}</code>
                      ) : (
                        <span className="hidden-text">••••••••••••</span>
                      )}
                      {showSensitive[selectedUser.id] && selectedUser.privateKey && (
                        <button onClick={() => copyToClipboard(selectedUser.privateKey)}>
                          <Copy size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Portfolio Value</h4>
                  <div className="balance-display">
                    <span className="balance-amount">${calculatePortfolioValue(selectedUser.assets).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        setShowUserModal(false);
                        handleEditAssets(selectedUser);
                      }}
                    >
                      <Edit size={14} />
                      Manage Assets
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowUserModal(false)}
                >
                  Close
                </button>
                <button 
                  className="btn btn-info"
                  onClick={() => {
                    setShowUserModal(false);
                    setShowNotificationModal(true);
                  }}
                >
                  <Bell size={16} />
                  Send Notification
                </button>
                <button 
                  className={`btn ${selectedUser.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                  onClick={() => {
                    handleToggleStatus(selectedUser);
                    setShowUserModal(false);
                  }}
                >
                  {selectedUser.status === 'active' ? 'Suspend User' : 'Activate User'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Balance Edit Modal */}
      <AnimatePresence>
        {showBalanceModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowBalanceModal(false)}>
            <motion.div 
              className="balance-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Edit Balance</h3>
                <button className="close-btn" onClick={() => setShowBalanceModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <p className="modal-description">
                  Update balance for {selectedUser.walletType} ({selectedUser.walletId})
                </p>

                <div className="current-balance">
                  <span className="label">Current Portfolio Value</span>
                  <span className="value">${calculatePortfolioValue(selectedUser.assets).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>

                <div className="form-group">
                  <label>New Balance</label>
                  <div className="balance-input">
                    <input
                      type="number"
                      step="0.0001"
                      value={balanceAmount}
                      onChange={(e) => setBalanceAmount(e.target.value)}
                      placeholder="0.00"
                    />
                    <span className="currency">{selectedUser.currency}</span>
                  </div>
                </div>

                <div className="quick-actions">
                  <button 
                    className="quick-btn"
                    onClick={() => setBalanceAmount((parseFloat(balanceAmount || 0) + 0.1).toFixed(4))}
                  >
                    <Plus size={14} /> 0.1
                  </button>
                  <button 
                    className="quick-btn"
                    onClick={() => setBalanceAmount((parseFloat(balanceAmount || 0) + 1).toFixed(4))}
                  >
                    <Plus size={14} /> 1
                  </button>
                  <button 
                    className="quick-btn"
                    onClick={() => setBalanceAmount((parseFloat(balanceAmount || 0) + 10).toFixed(4))}
                  >
                    <Plus size={14} /> 10
                  </button>
                  <button 
                    className="quick-btn minus"
                    onClick={() => setBalanceAmount(Math.max(0, parseFloat(balanceAmount || 0) - 0.1).toFixed(4))}
                  >
                    <Minus size={14} /> 0.1
                  </button>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowBalanceModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleUpdateBalance}
                >
                  Update Balance
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Single User Notification Modal */}
      <AnimatePresence>
        {showNotificationModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowNotificationModal(false)}>
            <motion.div 
              className="notification-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Send Notification</h3>
                <button className="close-btn" onClick={() => setShowNotificationModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <p className="modal-description">
                  Send notification to {selectedUser.walletType} ({selectedUser.walletId})
                </p>

                <div className="form-group">
                  <label>Notification Type</label>
                  <div className="type-selector">
                    {['info', 'success', 'warning', 'error'].map(type => (
                      <button
                        key={type}
                        className={`type-btn ${type} ${notificationForm.type === type ? 'active' : ''}`}
                        onClick={() => setNotificationForm(prev => ({ ...prev, type }))}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={notificationForm.title}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Notification title..."
                  />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    value={notificationForm.message}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Write your message..."
                    rows={4}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowNotificationModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSendNotification}
                >
                  <Send size={16} />
                  Send Notification
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Bulk Notification Modal */}
      <AnimatePresence>
        {showBulkNotificationModal && (
          <div className="modal-overlay" onClick={() => setShowBulkNotificationModal(false)}>
            <motion.div 
              className="notification-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Send Bulk Notification</h3>
                <button className="close-btn" onClick={() => setShowBulkNotificationModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <p className="modal-description">
                  Send notification to {selectedUsers.length > 0 ? `${selectedUsers.length} selected users` : 'all users'}
                </p>

                <div className="form-group">
                  <label>Notification Type</label>
                  <div className="type-selector">
                    {['info', 'success', 'warning', 'error'].map(type => (
                      <button
                        key={type}
                        className={`type-btn ${type} ${notificationForm.type === type ? 'active' : ''}`}
                        onClick={() => setNotificationForm(prev => ({ ...prev, type }))}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={notificationForm.title}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Notification title..."
                  />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    value={notificationForm.message}
                    onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Write your message..."
                    rows={4}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowBulkNotificationModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSendBulkNotification}
                >
                  <Send size={16} />
                  Send to {selectedUsers.length > 0 ? selectedUsers.length : 'All'} Users
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* User Assets Management Modal */}
      <AnimatePresence>
        {showAssetsModal && selectedUser && (
          <div className="modal-overlay" onClick={() => setShowAssetsModal(false)}>
            <motion.div 
              className="assets-modal glass-card"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>
                  <Wallet size={20} />
                  Manage User Assets
                </h3>
                <button className="close-btn" onClick={() => setShowAssetsModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                <div className="user-info-banner">
                  <div className="user-avatar-large">
                    {selectedUser.walletName?.slice(0, 2).toUpperCase() || 'W3'}
                  </div>
                  <div className="user-details">
                    <span className="user-name-lg">{selectedUser.walletName}</span>
                    <span className="wallet-type-sm">{selectedUser.walletType}</span>
                    <span className="wallet-id-sm">{selectedUser.walletId}</span>
                  </div>
                  <div className="portfolio-value">
                    <span className="label">Total Portfolio</span>
                    <span className="value">${calculatePortfolioValue(userAssets).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {pricesLoading && (
                  <div style={{ textAlign: 'center', padding: '12px', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
                    Loading live prices...
                  </div>
                )}

                <div className="assets-grid">
                  <div className="assets-grid-header">
                    <span>Asset</span>
                    <span>Current Value</span>
                    <span>Set / Adjust</span>
                  </div>
                  
                  {availableCryptoAssets.map(asset => {
                    const cryptoAmount = userAssets[asset.symbol] || 0;
                    const price = livePrices[asset.symbol] || 0;
                    const currentUsd = cryptoAmount * price;
                    
                    return (
                      <div key={asset.symbol} className="asset-row">
                        <div className="asset-info">
                          <img src={asset.icon} alt={asset.symbol} className="asset-icon-sm" />
                          <div className="asset-names">
                            <span className="asset-symbol">{asset.symbol}</span>
                            <span className="asset-name">{asset.name}</span>
                            {price > 0 && <span className="asset-price-hint">Rate: ${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span>}
                          </div>
                        </div>
                        <div className="asset-current-value">
                          <span className="current-usd">${currentUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          <span className="current-crypto">
                            {cryptoAmount > 0 
                              ? `${cryptoAmount < 0.001 ? cryptoAmount.toFixed(8) : cryptoAmount < 1 ? cryptoAmount.toFixed(6) : cryptoAmount.toFixed(4)} ${asset.symbol}`
                              : `0 ${asset.symbol}`
                            }
                          </span>
                        </div>
                        <div className="asset-adjust-controls">
                          <div className="asset-amount-input">
                            <input
                              type="number"
                              step="any"
                              min="0"
                              value={cryptoAmounts[asset.symbol] ?? ''}
                              onChange={(e) => handleSetAssetCrypto(asset.symbol, e.target.value)}
                              placeholder={cryptoAmount > 0 ? cryptoAmount.toString() : '0'}
                            />
                            <span className="asset-symbol-suffix">{asset.symbol}</span>
                          </div>
                          <div className="asset-amount-input">
                            <span className="asset-symbol-prefix">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={usdAmounts[asset.symbol] ?? ''}
                              onChange={(e) => handleSetAssetUsd(asset.symbol, e.target.value)}
                              placeholder={currentUsd > 0 ? currentUsd.toFixed(2) : '0.00'}
                            />
                          </div>
                          <div className="adjust-buttons">
                            <button className="adj-btn plus" onClick={() => handleAdjustAssetUsd(asset.symbol, 100)} title="Add $100">+100</button>
                            <button className="adj-btn plus" onClick={() => handleAdjustAssetUsd(asset.symbol, 500)} title="Add $500">+500</button>
                            <button className="adj-btn plus" onClick={() => handleAdjustAssetUsd(asset.symbol, 1000)} title="Add $1,000">+1k</button>
                            <button className="adj-btn minus" onClick={() => handleAdjustAssetUsd(asset.symbol, -100)} title="Remove $100">-100</button>
                            <button className="adj-btn minus" onClick={() => handleAdjustAssetUsd(asset.symbol, -500)} title="Remove $500">-500</button>
                            <button className="adj-btn minus" onClick={() => handleAdjustAssetUsd(asset.symbol, -1000)} title="Remove $1,000">-1k</button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="quick-set-section">
                  <span className="quick-set-label">Quick Set:</span>
                  <div className="quick-set-buttons">
                    <button 
                      className="quick-set-btn"
                      onClick={() => {
                        const usdVals = { BTC: 30000, ETH: 10000, SOL: 5000 };
                        const assets = {};
                        Object.entries(usdVals).forEach(([s, v]) => { assets[s] = livePrices[s] ? v / livePrices[s] : 0; });
                        setUserAssets(assets);
                        setUsdAmounts({});
                        setCryptoAmounts({});
                      }}
                    >
                      Starter ($45k)
                    </button>
                    <button 
                      className="quick-set-btn"
                      onClick={() => {
                        const usdVals = { BTC: 80000, ETH: 30000, SOL: 15000, BNB: 10000, MATIC: 5000 };
                        const assets = {};
                        Object.entries(usdVals).forEach(([s, v]) => { assets[s] = livePrices[s] ? v / livePrices[s] : 0; });
                        setUserAssets(assets);
                        setUsdAmounts({});
                        setCryptoAmounts({});
                      }}
                    >
                      Pro ($140k)
                    </button>
                    <button 
                      className="quick-set-btn"
                      onClick={() => {
                        const usdVals = { BTC: 150000, ETH: 50000, SOL: 30000, BNB: 20000, XRP: 15000, ADA: 10000, LINK: 10000 };
                        const assets = {};
                        Object.entries(usdVals).forEach(([s, v]) => { assets[s] = livePrices[s] ? v / livePrices[s] : 0; });
                        setUserAssets(assets);
                        setUsdAmounts({});
                        setCryptoAmounts({});
                      }}
                    >
                      Whale ($285k)
                    </button>
                    <button
                      className="quick-set-btn clear"
                      onClick={() => { setUserAssets({}); setUsdAmounts({}); setCryptoAmounts({}); }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setShowAssetsModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSaveAssets}
                >
                  <Check size={16} />
                  Save Assets
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 2FA Verification Modal for Seed Phrase */}
      {showTfaModal && (
        <div className="modal-overlay" onClick={() => setShowTfaModal(false)}>
          <motion.div
            className="tfa-modal glass-card"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="tfa-modal-header">
              <KeyRound size={24} />
              <h3>2FA Verification Required</h3>
            </div>
            <p className="tfa-modal-desc">Enter your authenticator code to view sensitive wallet data.</p>
            <div className="tfa-input-row" style={{ justifyContent: 'center' }}>
              <input
                type="text"
                value={tfaCode}
                onChange={(e) => { setTfaCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setTfaError(''); }}
                placeholder="000000"
                maxLength={6}
                inputMode="numeric"
                className="tfa-code-input"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleTfaVerifyAction()}
              />
            </div>
            {tfaError && <p className="tfa-error" style={{ textAlign: 'center' }}><AlertCircle size={14} /> {tfaError}</p>}
            <div className="tfa-modal-actions">
              <button className="btn btn-ghost" onClick={() => { setShowTfaModal(false); setTfaCode(''); setTfaError(''); }}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleTfaVerifyAction} disabled={tfaLoading}>
                {tfaLoading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;

